import to from "@/lib/to"
import {CLIENT_EVENT} from "@/lib/Pusher"
import {VIRTUAL_REQUEST_TYPE_MESSAGES} from "@/lib/config"
import {mapMutations} from "vuex"

export default {
  data() {
    return {
      roomNotifications: {},
      notificationAudio: null,
      notificationRepeatCount: 0,
      notificationMaxRepeats: 1
    }
  },

  mounted() {
    this.notificationAudio = new Audio()
    this.notificationAudio.addEventListener('ended', this.handleNotificationAudioEnded)
  },

  beforeUnmount() {
    if (this.notificationAudio) {
      this.notificationAudio.removeEventListener('ended', this.handleNotificationAudioEnded)
      this.notificationAudio.pause()
    }
  },

  methods: {
    ...mapMutations('LiveVideoStore', ['setShowLiveVideoOverlay', 'setMeetingMetaData']),
    initializeRoomNotifications(hospitalChannel) {
      if (!hospitalChannel) return

      hospitalChannel.bind(CLIENT_EVENT.ROOM_NOTIFICATION_ACCEPTED, this.handleRoomNotificationAccepted)
      hospitalChannel.bind(CLIENT_EVENT.ROOM_NOTIFICATION, this.handleRoomNotification)
    },

    async handleRoomNotificationAccepted(params) {
      if (this.$route.name == 'room-notification') {
        return;
      }

      this.clearAudio()
      const { patientId, userId, userName, level, patientName, roomName, patientLink, typeMessage, type, divisionId } = params

      if (!this.$store.state.currentUser.divisions.find(x => x.id == divisionId)) {
        return
      }

      if (this.roomNotifications[patientId]) {
        this.roomNotifications[patientId].forEach(snack => {
          this.skipDismiss = true
          snack.close()
        })
        this.roomNotifications[patientId] = []
      }

      if (this.$store.state.currentUser.id == userId) {
        return;
      }

      if (!this.$store.state.currentUser.virtual_request_types.includes(type)) {
        return;
      }

      this.$buefy.snackbar.open({
        queue: false,
        type: `${level} room-notification`,
        position: 'is-top',
        cancelText: 'Dismiss',
        indefinite: true,
        actionText: null,
        message: `${userName} Accepted - ${typeMessage} Request: Room ${roomName}
                    (<a href="${patientLink}">${this.$helpers.mask('patient_name', patientName)}</a>)`
      })
    },

    async handleRoomNotification({ virtualRequestId, patientId, type }) {

      if (this.$route.name == 'room-notification') {
        return;
      }

      if (!this.$store.state.currentUser.virtual_request_types.includes(type)) {
        return;
      }

      const [error, resp] = await to(this.$http.post(`/virtual_requests/${virtualRequestId}/receive`, {}, { skipLoading: true }))
      if (!error && resp.data?.status == 'denied') {
        return;
      }

      if (!this.roomNotifications[patientId]) {
        this.roomNotifications[patientId] = []
      }

      const levels = {
        "low": "is-success",
        "medium": "is-warning",
        "high": "is-danger"
      }

      const [err, response] = await to(this.$http.get(`/virtual_requests/${virtualRequestId}`))
      if (!err) {
        const { data: request } = response
        const level = levels[request.priority]
        const patientLink = this.$router.resolve({
          name: 'patients.overview.edit',
          params: { id: request.patient.id },
          query: { division_id: request.division.id }
        }).fullPath;

        if (this.$store.state.currentUser.audio_ping_enabled) {
          if (request.type_details?.ui_sound) {
            this.playNotificationSound(
              request.type_details.ui_sound,
              request.type_details.ui_sound_repeat_count || 1
            )
          }
        }

        const close = this.$buefy.snackbar.open({
          queue: false,
          type: `${level} room-notification`,
          position: 'is-top',
          cancelText: 'Dismiss',
          actionText: 'Video Visit',
          indefinite: true,
          onClose: () => {
            console.log('skipdismiss', this.skipDismiss)
            if (this.skipDismiss) {
              this.skipDismiss = false
            } else {
              to(this.$http.post(`/virtual_requests/${virtualRequestId}/dismiss`))
            }
            this.clearAudio()
          },
          message: `${request.type_details.label} Request for ${request.assignee != null ? request.assignee.name + '. ' : ''} Room ${request.room.name} -
                    ${request.division.name} (<a href="${patientLink}">${this.$helpers.mask('patient_name', request.patient.display_name)}</a>)`,

          onAction: async () => {
            this.skipDismiss = true
            this.clearAudio()
            to(this.$http.post(`/virtual_requests/${virtualRequestId}/accept`))

            to(this.$http.post('/pusher/trigger_hospital', {
              event: CLIENT_EVENT.ROOM_NOTIFICATION_ACCEPTED,
              data: {
                type,
                level,
                patientId,
                userName: this.$store.state.currentUser.name,
                userId: this.$store.state.currentUser.id,
                patientName: request.patient.display_name,
                roomName: request.room.name,
                patientLink: patientLink,
                typeMessage: VIRTUAL_REQUEST_TYPE_MESSAGES[type],
                divisionId: request.division.id
              }
            }, {
              skipLoading: true,
              skipDivisions: true
            }));

            const [err, meetingResponse] = await to(this.$http.get(`/meetings/new?patient_id=${request.patient.id}`))
            if (!err) {
              const { data } = meetingResponse
              this.setMeetingMetaData({
                device: data?.device,
                kiosk_mode: data.kiosk_mode,
                room_id: data.room_id,
                patient: request.patient,
                patient_monitor: data?.patient_monitor,
                video_reason_id: request.type_details.video_reason_id
              })
              this.setShowLiveVideoOverlay(true)
            }
          }
        })
        this.roomNotifications[patientId].push(close)
      }
    },

    clearAudio() {
      if (this.notificationAudio) {
        this.notificationAudio.pause()
        this.notificationAudio.src = ''
        this.notificationRepeatCount = 0
        this.notificationMaxRepeats = 1
      }
    },

    playNotificationSound(soundName, repeatCount) {
      if (!soundName || !this.notificationAudio) return

      this.notificationAudio.src = new URL(`../../assets/audio/topics/${soundName}.mp3`, import.meta.url).href
      this.notificationRepeatCount = 1
      this.notificationMaxRepeats = repeatCount || 1
      this.notificationAudio.play().catch(err => {
        console.error('Failed to play notification sound:', err)
      })
    },

    handleNotificationAudioEnded() {
      if (this.notificationRepeatCount < this.notificationMaxRepeats) {
        this.notificationRepeatCount++
        this.notificationAudio.play().catch(err => {
          console.error('Failed to replay notification sound:', err)
        })
      } else {
        this.notificationRepeatCount = 0
        this.notificationMaxRepeats = 1
      }
    }
  }
}
